import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "modal-link__title" }
const _hoisted_2 = { class: "modal-link__label" }

import { computed, ref } from 'vue';
import { Editor } from '@tiptap/vue-3';
import { ElButton } from 'element-plus';
import { useI18n } from 'vue-i18n';
import { SuccessFilled } from '@element-plus/icons-vue';
import { useRegExp } from '@/constants/default';
import UiInput from '../UiInput.vue';
import ModalSetLink from './Addlink.vue';
import Bold from './icons/Bold.vue';
import Italic from './icons/Italic.vue';
import Strikethrough from './icons/Strikethrough.vue';
import CodeView from './icons/CodeView.vue';
import H1 from './icons/H1.vue';
import H2 from './icons/H2.vue';
import IconLink from './icons/Link.vue';
import Paragraph from './icons/Paragraph.vue';
import ListUnordered from './icons/ListUnordered.vue';
import ListOrdered from './icons/ListOrdered.vue';
import CodeBox from './icons/CodeBoxFill.vue';
import DoubleQuotes from './icons/DoubleQuotes.vue';
import Separator from './icons/Separator.vue';
import TextWrap from './icons/TextWrap.vue';
import FormatClear from './icons/FormatClear.vue';
import ArrowGoBackLine from './icons/ArrowGoBackLine.vue';
import ArrowGoForwardLine from './icons/ArrowGoForwardLine.vue';
import MenuItem from './MenuItem.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'MenuBar',
  props: {
    editor: {}
  },
  setup(__props: any) {

const { t } = useI18n();

const props = __props;

const $modal = ref<HTMLDialogElement | null>(null);
const url = ref<string>('');

const validateURL = computed(() => useRegExp.correctUrl.test(url.value));

const setLink = () => {
  url.value = props.editor.getAttributes('link').href || '';
  $modal.value?.showModal();
};

const handleClose = () => {
  $modal.value?.close();
};

const handleUpdate = (newUrl: string): void => {
  url.value = newUrl;
};

const handleSave = (): void => {
  if (url.value && !validateURL.value) {
    return;
  }

  const newUrl = url.value;
  url.value = '';
  handleClose();

  // empty
  if (newUrl === '') {
    props.editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .unsetLink()
      .run();

    return;
  }

  // update link
  props.editor
    .chain()
    .focus()
    .extendMarkRange('link')
    .setLink({ href: newUrl, target: '_blank' })
    .run();
};

const items = [
  {
    icon: Bold,
    title: t('btnBold'),
    action: () => props.editor.chain().focus().toggleBold().run(),
    isActive: () => props.editor.isActive('bold'),
  },
  {
    icon: Italic,
    title: t('btnItalic'),
    action: () => props.editor.chain().focus().toggleItalic().run(),
    isActive: () => props.editor.isActive('italic'),
  },
  {
    icon: Strikethrough,
    title: t('btnCross'),
    action: () => props.editor.chain().focus().toggleStrike().run(),
    isActive: () => props.editor.isActive('strike'),
  },
  {
    icon: IconLink,
    title: t('btnAddLink'),
    action: setLink,
    isActive: () => props.editor.isActive('link'),
  },
  {
    icon: CodeView,
    title: t('btnAddCode'),
    action: () => props.editor.chain().focus().toggleCode().run(),
    isActive: () => props.editor.isActive('code'),
  },
  {
    type: 'divider',
  },
  {
    icon: H1,
    title: `${t('btnH')} 1`,
    action: () => props.editor.chain().focus()
      .toggleHeading({ level: 1 }).run(),
    isActive: () => props.editor.isActive('heading', { level: 1 }),
  },
  {
    icon: H2,
    title: `${t('btnH')} 2`,
    action: () => props.editor.chain().focus()
      .toggleHeading({ level: 2 }).run(),
    isActive: () => props.editor.isActive('heading', { level: 2 }),
  },
  {
    icon: Paragraph,
    title: t('btnP'),
    action: () => props.editor.chain().focus().setParagraph().run(),
    isActive: () => props.editor.isActive('paragraph'),
  },
  {
    icon: ListUnordered,
    title: t('btnUnnumberedList'),
    action: () => props.editor.chain().focus().toggleBulletList().run(),
    isActive: () => props.editor.isActive('bulletList'),
  },
  {
    icon: ListOrdered,
    title: t('btnNumberedList'),
    action: () => props.editor.chain().focus().toggleOrderedList().run(),
    isActive: () => props.editor.isActive('orderedList'),
  },
  {
    icon: CodeBox,
    title: t('btnCode'),
    action: () => props.editor.chain().focus().toggleCodeBlock().run(),
    isActive: () => props.editor.isActive('codeBlock'),
  },
  {
    type: 'divider',
  },
  {
    icon: DoubleQuotes,
    title: t('btnQuote'),
    action: () => props.editor.chain().focus().toggleBlockquote().run(),
    isActive: () => props.editor.isActive('blockquote'),
  },
  {
    icon: Separator,
    title: t('btnHorizontalDivider'),
    action: () => props.editor.chain().focus().setHorizontalRule().run(),
  },
  {
    type: 'divider',
  },
  {
    icon: TextWrap,
    title: t('btnNewLine'),
    action: () => props.editor.chain().focus().setHardBreak().run(),
  },
  {
    icon: FormatClear,
    title: t('btnClear'),
    action: () => props.editor.chain()
      .focus()
      .clearNodes()
      .unsetAllMarks()
      .run(),
  },
  {
    type: 'divider',
  },
  {
    icon: ArrowGoBackLine,
    title: t('btnCancel'),
    action: () => props.editor.chain().focus().undo().run(),
  },
  {
    icon: ArrowGoForwardLine,
    title: t('btnRepeat'),
    action: () => props.editor.chain().focus().redo().run(),
  },
];

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(items, (item, index) => {
      return (_openBlock(), _createElementBlock(_Fragment, null, [
        (item.type === 'divider')
          ? (_openBlock(), _createElementBlock("div", {
              key: `divider${index}`,
              class: "divider"
            }))
          : (_openBlock(), _createBlock(MenuItem, {
              key: index,
              icon: item.icon,
              title: item.title || '',
              action: item.action || function() {},
              isActive: item.isActive
            }, null, 8, ["icon", "title", "action", "isActive"]))
      ], 64))
    }), 64)),
    _createVNode(ModalSetLink, {
      ref_key: "$modal",
      ref: $modal
    }, {
      header: _withCtx(() => [
        _createElementVNode("h3", _hoisted_1, _toDisplayString(_unref(t)('btnSetLink')), 1)
      ]),
      buttons: _withCtx(() => [
        _createVNode(_unref(ElButton), { onClick: handleClose }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('cancel')), 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(ElButton), {
          type: "primary",
          icon: _unref(SuccessFilled),
          onClick: handleSave
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('save')), 1)
          ]),
          _: 1
        }, 8, ["icon"])
      ]),
      default: _withCtx(() => [
        _createElementVNode("label", _hoisted_2, [
          _cache[0] || (_cache[0] = _createTextVNode(" URL ")),
          _createVNode(UiInput, {
            value: url.value,
            class: "modal-link__input",
            type: "url",
            error: !validateURL.value && url.value !== '',
            onInput: handleUpdate
          }, null, 8, ["value", "error"])
        ])
      ]),
      _: 1
    }, 512)
  ]))
}
}

})